import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Chip
} from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import { useNavigate } from 'react-router-dom';

// Components
import TaxServiceCard from './components/TaxServiceCard';
import RequestTaxServiceDialog from './components/RequestTaxServiceDialog';

// Mock data
import {
  taxServiceCategories,
  mockTaxServices,
  getTaxProfile,
  simulateDelay
} from '../../../mockData/orgDocIndex';

// MockBadge inline component for mock data indication
const MockBadge = () => (
  <Chip
    label="MOCK"
    size="small"
    color="warning"
    sx={{ ml: 1, fontSize: '10px', height: '20px' }}
  />
);

/**
 * Tax Services Page
 * Service catalog for UAE Corporate Tax and VAT services
 *
 * Features:
 * - Service categories (CT, VAT, Compliance)
 * - Service cards with request action
 * - Service request dialog
 *
 * @mock Uses mock data during UI-First phase
 */
const TaxServicesPage = () => {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [taxProfile, setTaxProfile] = useState(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // TODO: Get actual organization ID from context/auth
  const organizationId = 'org-001';

  // Categories with "ALL" option
  const categories = [
    { id: 'ALL', code: 'ALL', name: 'All Services', color: '#666' },
    ...taxServiceCategories
  ];

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      await simulateDelay(300);

      // Load tax profile for dialog context
      const profile = getTaxProfile(organizationId);
      setTaxProfile(profile);
    } catch (error) {
      console.error('Error loading tax data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle category change
  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  // Filter services by category
  const filteredServices = selectedCategory === 'ALL'
    ? mockTaxServices.filter(s => s.isActive)
    : mockTaxServices.filter(s => s.categoryCode === selectedCategory && s.isActive);

  // Handle request service
  const handleRequestService = (service) => {
    setSelectedService(service);
    setRequestDialogOpen(true);
  };

  // Handle service requested
  const handleServiceRequested = (request) => {
    setRequestDialogOpen(false);
    setSelectedService(null);
    // Navigate to requests page after submitting
    navigate('/CST-TaxServices/requests');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              Request Service
            </Typography>
            <MockBadge />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlined />}
              onClick={() => navigate('/CST-TaxServices/status')}
            >
              Tax Status
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlined />}
              onClick={() => navigate('/CST-TaxServices/requests')}
            >
              My Requests
            </Button>
          </Box>
        </Box>

        {/* Services Section */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              sx={{ px: 2 }}
            >
              {categories.map((category) => (
                <Tab
                  key={category.code}
                  value={category.code}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category.name}
                      {category.code !== 'ALL' && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: category.color
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Service Cards Grid */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {filteredServices.map((service) => (
                <Grid item xs={12} md={6} lg={4} key={service.id}>
                  <TaxServiceCard
                    service={service}
                    onRequest={() => handleRequestService(service)}
                    organizationId={organizationId}
                  />
                </Grid>
              ))}
            </Grid>

            {filteredServices.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">
                  No services available in this category
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {/* Request Service Dialog */}
      <RequestTaxServiceDialog
        open={requestDialogOpen}
        onClose={() => {
          setRequestDialogOpen(false);
          setSelectedService(null);
        }}
        service={selectedService}
        taxProfile={taxProfile}
        organizationId={organizationId}
        onSubmit={handleServiceRequested}
      />
    </>
  );
};

export default TaxServicesPage;
